import { createLogger } from "../helper/logger.js";
import { pdStore } from "../store/mini-rx.store.js";
//#region src/services/fire-once-service.ts
/**
* FireOnceService - Simple service for fire-once action dispatching
*
* Provides a simple mechanism to ensure actions are only dispatched once
* per session. Useful for initial data loading that should not be repeated.
*
* @example
* ```typescript
* // In a component
* connectedCallback() {
*   fireOnceService.fireOnce("profile-data", loadProfileRemote.action());
* }
*
* // Check if already fired
* if (!fireOnceService.hasFired("profile-data")) {
*   // Do something
* }
*
* // Reset on logout
* fireOnceService.reset();
* ```
*/
var logger = createLogger("FireOnceService");
var FireOnceService = class {
	constructor() {
		this._firedKeys = /* @__PURE__ */ new Set();
	}
	/**
	* Execute an action only if it hasn't been fired before for the given key.
	*
	* @param key - Unique identifier for this fire-once action
	* @param action - The action to dispatch
	* @returns true if action was dispatched, false if already fired
	*/
	fireOnce(key, action) {
		if (this._firedKeys.has(key)) {
			logger.debug(`Key already fired, skipping: ${key}`);
			return false;
		}
		this._firedKeys.add(key);
		pdStore().dispatch(action);
		logger.debug(`Action fired for key: ${key}`);
		return true;
	}
	/**
	* Check if a key has been fired.
	*
	* @param key - The key to check
	* @returns true if the key has been fired
	*/
	hasFired(key) {
		return this._firedKeys.has(key);
	}
	/**
	* Reset a specific key or all keys.
	* Useful for logout scenarios or testing.
	*
	* @param key - Optional key to reset. If not provided, resets all keys.
	*/
	reset(key) {
		if (key) {
			this._firedKeys.delete(key);
			logger.debug(`Reset key: ${key}`);
		} else {
			this._firedKeys.clear();
			logger.debug("Reset all keys");
		}
	}
	/**
	* Get all fired keys (for debugging).
	*
	* @returns Array of all fired keys
	*/
	getFiredKeys() {
		return Array.from(this._firedKeys);
	}
};
var fireOnceService = new FireOnceService();
//#endregion
export { fireOnceService };
